// Agentic chat-log auditor. Drives a sub-agent that reads one (or
// any number of) chats in this window via the audit toolkit, walks
// tool-call lineage, and produces structured findings — bug reports
// the operator can file upstream, improvement proposals for the
// dashboard / agent infrastructure.
//
// Mirrors kernelSaveAgent's shape so the chat-tree UI renders the
// audit's progress the same way it renders /save-kernel: a slot
// with status / activity / tools-running list / preview, plus a
// transcript toggle if the user wants to drill into the child's
// turn-by-turn trace.

import { mirrorTranscript } from "./agentTranscript.js";
import { runSubAgent } from "./subAgent.js";
import { forwardChildEvent } from "./subAgentTools.js";
import { listChatsTool, readChatTranscriptTool, readToolCallTool } from "./auditTools.js";

// Static toolkit. Other tools (execute_python, render_block,
// inspect_subagent, every Linear MCP tool) are pulled by name from
// the parent's live registry at start time — they need the parent's
// closures (Pyodide namespace, render tracker, MCP transport) and
// can't be constructed standalone.
const STATIC_AUDIT_TOOLS = [listChatsTool, readChatTranscriptTool, readToolCallTool];

const DYNAMIC_TOOL_NAMES = ["execute_python", "render_block", "inspect_subagent"];

const LINEAR_TOOL_PREFIX = "mcp__claude_ai_Linear__";

const AUDIT_OUTPUT_SCHEMA = {
    type: "object",
    required: ["findings", "summary"],
    properties: {
        findings: {
            type: "array",
            items: {
                type: "object",
                required: ["severity", "category", "title", "body"],
                properties: {
                    severity: { type: "string", enum: ["bug", "improvement", "observation"] },
                    category: { type: "string", description: "e.g. tool-description, compaction, preview-cutoff, staging, retries, cost, schema" },
                    title: { type: "string" },
                    body: { type: "string", description: "concrete + actionable + cites tool_call_ids" },
                    evidence_tool_call_ids: {
                        type: "array",
                        items: { type: "string" },
                    },
                    linear_issue_id: {
                        type: "string",
                        description: "set when this finding was filed in Linear",
                    },
                },
            },
        },
        summary: { type: "string" },
    },
};

const AUDIT_SYSTEM_PROMPT = [
    "You are a chat-log auditor for an Origin dashboard / AI-agent platform. Your job is to walk one or more chat transcripts in this workspace and produce concrete, actionable bug reports and improvement proposals — the kind the operator can integrate upstream into the dashboard and agent infrastructure.",
    "",
    "Workflow:",
    "  1. `list_chats` to confirm the target chat exists and see what else is open in the window. Cross-session patterns matter — a tool-description ambiguity that bites in chat A probably bit in chat B too.",
    "  2. `read_chat_transcript` with view='both' on the target chat. The `ui` view shows what the human saw; the `llm` view shows what the model actually had in context after compaction + convertToLlm. Bugs hide in the delta — `synthesized: true` staged inputs, compaction stubs, results the user could see but the model couldn't (or vice versa).",
    "  3. For any tool call that looks suspicious (errors, retries, looped reformulations, unexpected args, missing from_tool_calls handoff), call `read_tool_call` with the tool_call_id to get its full record and data lineage. Walk `lineage.producers` / `lineage.consumers` / `lineage.staged_for` to understand data flow.",
    "  4. Use `execute_python` for transcript-wide pattern detection — count tool-call frequencies, regex over args, identify retry sequences, etc. The audit tools' results are row-shapeable; reference them via `from_tool_calls`.",
    "  5. For `spawn_subagent` / `spawn_batch` invocations, `inspect_subagent` (if available) drills into the child transcript. The `read_tool_call` lineage walker also recurses into child tool calls.",
    "",
    "Failure modes worth flagging (non-exhaustive):",
    "  • Tool errors with patterns — same error twice = the model didn't learn from the first; same error across chats = a tool description / schema problem worth filing.",
    "  • Looping behavior — model reformulating the same query 3+ times before succeeding.",
    "  • Tool selection mistakes — model picked execute_python when analytics_query would have worked, or pasted rows into args.data when from_tool_calls was the right move, or called expand_preview when from_tool_calls would have been cheaper.",
    "  • Description ambiguity — model misread a tool description (visible when the model's args don't match what the description called for).",
    "  • Preview cutoff hiding load-bearing rows — large analytics_query results where the model reasoned only off the head sample and missed a tail row that mattered.",
    "  • Staged-input mishandling — synthesized tool results that the child didn't actually use, or that the parent staged with a wrong slice.",
    "  • Render failures — render_block specs that didn't produce a meaningful visual, or were never followed by a suggest_kernel when they should have been.",
    "  • Cost outliers — turns that burned unusually high tokens for the work done.",
    "  • Schema mismatches — analytics_query specs that hit field-name errors.",
    "",
    "Output expectations:",
    "  • Each finding has: severity (bug | improvement | observation), category (tool-description | compaction | preview-cutoff | staging | retries | cost | schema | …), title, body (concrete + actionable + cites tool_call_ids), evidence_tool_call_ids[].",
    "  • Be specific — \"the model retried analytics_query 4 times in turn 3 because field X was misspelled\" is useful; \"the model seems confused\" is not.",
    "  • Prefer high-signal findings over exhaustive lists. 3 sharp issues > 15 vague ones.",
    "  • If a Linear MCP tool is in your toolkit, file `severity: bug` findings as Linear issues; include chat_id and evidence_tool_call_ids in the issue body. Stamp `linear_issue_id` on each finding you filed. If Linear isn't available, surface findings via return_result only — the operator will see them.",
    "  • Optionally call `render_block` with a `rows` or `table` spec to make the findings visually skimmable for the user.",
    "  • Call `return_result` exactly once with { findings, summary } when you're done.",
].join("\n");

// Entry point. Returns { promise, abort }.
//   promise resolves to { ok, result?, error? }.
//   abort cancels the in-flight sub-agent (UI's STOP button).
//   onState receives slot deltas the chat-tree UI mirrors.
export function startChatAudit({ targetChatId, parentTools, thinkingLevel, onState }) {
    const controller = new AbortController();
    const slot = makeSlot();
    const emit = () => { try { onState?.({ ...slot }); } catch { /* noop */ } };
    emit();

    // Pull dynamic tools by name from the parent's live registry.
    // Tools the parent doesn't have (e.g. inspect_subagent in a
    // bare chat) drop silently — the auditor's workflow degrades
    // gracefully without them.
    const byName = new Map((parentTools || []).map((t) => [t.name, t]));
    const dynamic = DYNAMIC_TOOL_NAMES.map((n) => byName.get(n)).filter(Boolean);
    const linear = (parentTools || []).filter((t) => typeof t.name === "string" && t.name.startsWith(LINEAR_TOOL_PREFIX));
    const tools = [...STATIC_AUDIT_TOOLS, ...dynamic, ...linear];

    const onEvent = (event) => {
        forwardChildEvent(slot, event);
        emit();
    };
    const onChildReady = (child) => {
        mirrorTranscript(slot, child);
        emit();
    };

    const promise = (async () => {
        const { status, result, error } = await runSubAgent({
            task: `Audit chat_id=${targetChatId}. Read its transcript in BOTH ui and llm views. Walk every tool call. Identify bugs and improvements. File findings via Linear when available; always return { findings, summary } via return_result.`,
            systemPrompt: AUDIT_SYSTEM_PROMPT,
            tools,
            outputSchema: AUDIT_OUTPUT_SCHEMA,
            signal: controller.signal,
            onEvent,
            onChildReady,
            // Default (primary) role — the audit is reasoning-heavy. Thread
            // the parent chat's selected depth so it reasons as deeply as
            // the user picked; null defers to subAgent's per-model fallback.
            thinkingLevel,
        });
        if (status === "aborted") {
            slot.status = "stopped";
            slot.activity = null;
            emit();
            return { ok: false, error: "audit cancelled" };
        }
        if (status !== "done" || !result || typeof result !== "object") {
            slot.status = "error";
            slot.activity = null;
            emit();
            return { ok: false, error: error?.message || "audit did not return findings" };
        }
        slot.status = "done";
        slot.activity = null;
        emit();
        return { ok: true, result };
    })();

    return {
        promise,
        abort: () => { try { controller.abort(); } catch { /* noop */ } },
    };
}

function makeSlot() {
    return {
        status: "running",
        activity: "starting…",
        tools: [],
        preview: "",
        messages: [],
    };
}
